import { ATTRIBUTES_REGEX } from '@/shared/utils/regex';
import { textEditorFonts } from '@/shared/utils/text-editor';
import { DOMSerializer } from '@tiptap/pm/model';
import { Editor } from '@tiptap/react';
import { TEMPLATE_ATTRIBUTES, TemplateAttribute } from './types';

export const checkIsFontSupportingOperation = (
  fontName: string,
  operation: 'bold' | 'italics' | 'boldItalics'
) => {
  if (operation === 'bold') {
    return textEditorFonts.find((f) => f.name === fontName)?.bold;
  } else if (operation === 'italics') {
    return textEditorFonts.find((f) => f.name === fontName)?.italic;
  } else if (operation === 'boldItalics') {
    return textEditorFonts.find((f) => f.name === fontName)?.boldItalic;
  }
  return false;
};

// A utility when the hook is not available
export const getEditorPlainText = (editor: Editor | null, optOutMessage?: string) => {
  if (!editor) {
    return '';
  }
  const { state } = editor;

  // Get the full document (not just the selection)
  const slice = state.doc.slice(0, state.doc.content.size); // From start to end of the document

  // Convert the selected content slice to DOM and then to HTML
  const fragment = DOMSerializer.fromSchema(editor.schema).serializeFragment(
    slice.content
  );

  // Create a temporary container to hold the HTML
  const temporaryDiv = document.createElement('div');
  temporaryDiv.appendChild(fragment);

  // const message = editor?.storage?.customData?.optOutMessage
  //   ? `${replaceLinkTagsWithActualLinks(replaceAttributesWithBraces(transformPTags(temporaryDiv.innerHTML)))}${editor.storage.customData.optOutMessage}`
  //   : `${replaceLinkTagsWithActualLinks(replaceAttributesWithBraces(transformPTags(temporaryDiv.innerHTML)))}`
  //   ;

  const message = optOutMessage
    ? `${replaceLinkTagsWithActualLinks(replaceAttributesWithBraces(transformPTags(temporaryDiv.innerHTML)))}\n${optOutMessage}`
    : `${replaceLinkTagsWithActualLinks(replaceAttributesWithBraces(transformPTags(temporaryDiv.innerHTML)))}`;
  return message;
};

// Replace {first_name} with Custom attributes
// eg: '{title}' to '<text-editor-attribute text="Title" key="title"></text-editor-attribute>'
// Disable URL auto-transforms
// Replace url with Custom attributes
// eg: 'some-url' to '<text-editor-link key="some-url"></text-editor-link>'
export const replaceRawAttributes = (text: string) => {
  const keys = TEMPLATE_ATTRIBUTES.map((a) => a.key);
  let transformedText = text;
  // console.log('replaceRawAttributes ', { text });

  // Disable URL auto-transforms
  // // Replace Tracked Link URLs
  // transformedText = transformedText.replace(PARTIAL_TRACKED_LINK_REGEX, (match) => {
  //   // console.log('replaceRawAttributes tracked link', { match, placeholder });
  //   if (match) {
  //     // console.log('replaceRawAttributes tracked link returning', `<text-editor-link link="${match}"></text-editor-link>`);
  //     return `<text-editor-link link="${match}"></text-editor-link>`;
  //   }
  //   return match;
  // });

  // Disable URL auto-transforms
  // // Replace URLs
  // transformedText = transformedText.replace(PARTIAL_URL_REGEX, (match) => {
  //   // console.log('replaceRawAttributes url', { match });
  //   if (match) {
  //     // console.log('replaceRawAttributes url returning', `<text-editor-link link="${match}"></text-editor-link>`);
  //     return `<text-editor-link link="${match}"></text-editor-link>`;
  //   }
  //   return match;
  // });

  // Replace Attributes
  transformedText = transformedText.replace(ATTRIBUTES_REGEX, (match, placeholder) => {
    // console.log('replaceRawAttributes attributes', { match, placeholder });
    const attribute = TEMPLATE_ATTRIBUTES.find(
      (a) => a.key === placeholder
    ) as TemplateAttribute;
    if (keys.includes(placeholder)) {
      return `<text-editor-attribute key="${attribute?.key}" text="${attribute.label}"></text-editor-attribute> `;
    }
    return match; // Leave the original placeholder if not in the allowed list
  });

  return transformedText;
};

// Replace Custom attributes with braces text
// eg: '<text-editor-attribute text="Title" key="title"></text-editor-attribute>' to '{title}'
export const replaceAttributesWithBraces = (html: string) => {
  // Replace <text-editor-attribute> tags with {key}
  const regex =
    /<text-editor-attribute\s+(?:key="([a-zA-Z0-9_]+)"\s+text="[^"]+"|text="[^"]+"\s+key="([a-zA-Z0-9_]+)")><\/text-editor-attribute>/g;

  return html.replace(regex, (match, key1, key2) => {
    const key = key1 || key2; // Use whichever key is found
    return `{${key}}`; // Replace the tag with {key}
  });
};

export const containsTemplateVariables = (text: string): boolean => {
  return /\{[a-zA-Z0-9_]+\}/.test(text);
};

// Replace Custom attributes with braces text
// eg: '<text-editor-link key="URL_HERE">URL_HERE</text-editor-link>' to 'URL_HERE'
export const replaceLinkTagsWithActualLinks = (html: string) => {
  // Match any text inside the link="" attribute within <text-editor-link>
  const regex = /<text-editor-link\s+link="([^"]*)"><\/text-editor-link>/g;

  // Replace the matched <text-editor-link> with just the URL
  return html.replace(regex, (match, key) => key);
};

// Remove all slashes
export const removeSlashes = (input: string) => {
  const output = input.replace(/\/+/g, '');

  return output;
};

// Replace <p> tags and trim whitespace
export const transformPTags = (input: string) => {
  const output = input
    // Add the contents of every Paragraph element + a new line char (\n)
    .replace(/<p>([\s\S]*?)<\/p>/g, '$1\n')
    // Remove the last \n
    .replace(/\n$/, '')
    // Replace empty Paragraph elements with a new line char (\n)
    .replace(/<p><\/p>/g, '\n');
  return output;
};
